// La mise en page dans Mon CV (Radar, issue #235) : le modèle des prochains
// CV, LE MÊME réglage que Réglages, « Mes CV adaptés » (`template` de
// `/api/settings`). Retour de Nicolas du 2026-10-21 : Mon CV ne montrait pas
// ce qui était choisi, et un choix fait ici doit se voir là-bas. Puis où en
// est « Ma mise en page », et le geste pour la préparer ou la refaire.
//
// Le CV affiché au-dessus suit le choix (`onModele`) : choisir « Deux
// colonnes » sans voir son CV changer ne disait pas ce qu'on venait de faire
// (retour du même jour). La page tient le modèle ; ce panneau le change.
//
// Elle se prépare seule au dépôt du PDF quand la formule l'ouvre. Ce panneau
// sert à la refaire si la première ne plaît pas, à dire qu'un CV venu de
// l'extension n'a pas de PDF à lire, et à montrer la formule qui l'ouvre.

import { useEffect } from 'react'
import { Check, LayoutTemplate, RefreshCw } from 'lucide-react'
import { get, post } from '../lib/api'
import { useT } from '../lib/i18n'
import { useData, ActionButton, PastilleTitre, useToast } from './common'

interface Etat {
  etat: 'aucune' | 'en_cours' | 'prete' | 'echec'
  existe: boolean
  utilisable: boolean
  essai: boolean
  avecPdf: boolean
}

/** Les modèles proposés aux prochains CV, dans l'ordre des Réglages. */
const MODELES = [
  { v: 'Moderne', nom: 'modeles.sobre', effet: 'config.modele.sobreEffet' },
  { v: 'Dates', nom: 'modeles.dates', effet: 'config.modele.datesEffet' },
  { v: 'Colonnes', nom: 'modeles.colonnes', effet: 'modeles.colonnes.note' },
  { v: 'Compact', nom: 'modeles.compact', effet: 'config.modele.compactEffet' },
  { v: 'Perso', nom: 'modeles.perso', effet: 'config.modele.persoEffet' },
] as const

/** Radar n'a plus ATS : « Sobre » en tient la place, comme dans les Réglages. */
export const modeleRadar = (brut?: string | null): string => (!brut || brut === 'ATS-Optimized' ? 'Moderne' : brut)

export function MaMiseEnPage({
  modele,
  onModele,
  onMiseEnPage,
}: {
  /** Le modèle des prochains CV, tenu par la page. */
  modele: string
  onModele: (v: string) => void
  /** Le modèle de « Ma mise en page », dès qu'il est connu (null sinon). */
  onMiseEnPage?: (modele: string | null) => void
}) {
  const t = useT()
  const toast = useToast()
  const data = useData(() => get<Etat & { modele: string | null }>('/api/mise-en-page'), [])
  const d = data.data
  useEffect(() => { onMiseEnPage?.(d?.modele ?? null) }, [d?.modele])
  const enCours = d?.etat === 'en_cours'
  useEffect(() => {
    if (!enCours) return
    const id = setInterval(() => data.reload(), 5000)
    return () => clearInterval(id)
  }, [enCours, data.reload])
  if (!d) return null

  const lancer = async () => {
    try {
      await post('/api/mise-en-page', {})
      data.reload()
    } catch (e: any) {
      toast(e.message, 'err')
    }
  }

  const choisir = async (v: string) => {
    if (v === modele) return
    // Sans Complet, « Ma mise en page » mène aux formules, comme sa vignette.
    if (v === 'Perso' && !d.utilisable) {
      window.location.hash = '#/abonnement'
      return
    }
    const avant = modele
    onModele(v)
    try {
      await post('/api/settings', { template: v })
      toast(t('modeles.parDefaut'))
    } catch (e: any) {
      onModele(avant)
      toast(e.message, 'err')
    }
  }
  const pris = MODELES.find((m) => m.v === modele)

  const phrase = !d.utilisable
    ? null
    : enCours
      ? t('miseEnPage.enCours')
      : d.etat === 'echec'
        ? t('miseEnPage.echec')
        : d.existe
          ? t('miseEnPage.prete')
          : d.essai
            ? t('miseEnPage.essai')
            : null

  return (
    <div className="panel" data-testid="ma-mise-en-page">
      <h4><PastilleTitre h={250}><LayoutTemplate size={16} /></PastilleTitre>{t('config.modeleLabel')}</h4>
      <p className="pnote">{t('miseEnPage.choix')}</p>
      <div className="regions" role="radiogroup" aria-label={t('config.modeleLabel')}>
        {MODELES.map((m) => (
          <button
            key={m.v}
            type="button"
            role="radio"
            aria-checked={modele === m.v}
            className={`region${modele === m.v ? ' on' : ''}`}
            onClick={() => void choisir(m.v)}
            data-testid={`mon-cv-modele-${m.v}`}
          >
            {modele === m.v && <Check size={12} strokeWidth={3} />}
            {t(m.nom)}
          </button>
        ))}
      </div>
      {pris && <p className="field-help">{t(pris.effet)}</p>}
      <p className="pnote"><b>{t('miseEnPage.titre')}</b> · {t('miseEnPage.texte')}</p>
      {phrase && <p className="pnote"><b>{phrase}</b></p>}
      {!d.utilisable ? (
        <p className="pnote">
          {t('miseEnPage.complet')} <a href="#/abonnement">{t('miseEnPage.formules')}</a>
        </p>
      ) : !d.avecPdf ? (
        // Sans PDF, rien à refaire ; une mise en page déjà prête reste.
        !d.existe && <p className="pnote">{t('miseEnPage.sansPdf')}</p>
      ) : (
        !enCours && (
          <div className="pnote">
            <ActionButton onClick={lancer}>
              <RefreshCw size={15} />
              {d.existe ? t('miseEnPage.refaire') : t('miseEnPage.preparer')}
            </ActionButton>
          </div>
        )
      )}
    </div>
  )
}
